import React, { useEffect, useState } from 'react';
import { MapPin, Gift, Search, SlidersHorizontal, ChevronRight, Calendar, Sparkles, Coffee, Heart, TrendingUp, ChevronDown } from 'lucide-react';
import { Store, UserProfile } from '../../types';
import { StoreCard } from '../StoreCard';
import { STORES, MOCK_CAFE_LISTS } from '../../data/mockData';
interface HomeScreenProps {
  user: UserProfile;
  onNavigate: (screen: any) => void;
  onStoreClick: (store: Store) => void;
}
export function HomeScreen({
  user,
  onNavigate,
  onStoreClick
}: HomeScreenProps) {
  const [activeHeroIndex, setActiveHeroIndex] = useState(0);
  // Mock data for new sections
  const categories = ['Cafes', 'Pizza', 'Chai', 'Desserts', 'Fast Food'];
  const nearbyStores = STORES.slice(0, 5);
  const worthTheTrip = STORES.slice(5, 10);
  const newOnKraya = STORES.slice(10, 15);
  const friendLists = MOCK_CAFE_LISTS?.filter(list => list.isShared) || [];
  // Mock user history state (toggle to true to see rewards)
  const hasHistory = false;
  const rewardStores = [{
    store: STORES[0],
    discount: '19% Off',
    reward: 'Free Coffee on Your Next Visit',
    lastVisit: 'May 2'
  }, {
    store: STORES[1],
    discount: '15% Off',
    reward: 'Free Miso Soup on Your Next Visit',
    lastVisit: 'May 5'
  }];
  // Auto-rotate hero banner
  useEffect(() => {
    const timer = setInterval(() => {
      setActiveHeroIndex(prev => (prev + 1) % 3);
    }, 4000);
    return () => clearInterval(timer);
  }, []);
  return <div className="pb-24 min-h-screen bg-[#FFFF66] relative overflow-hidden font-sans" data-id="element-1079">
      {/* Sticky Header Area */}
      <div className="sticky top-0 z-40 bg-[#FFFF66]/95 backdrop-blur-md pt-6 pb-4 px-4 border-b border-black/5" data-id="element-1080">
        {/* Greeting & Location */}
        <div className="flex items-center justify-between mb-4" data-id="element-1081">
          <div data-id="element-1082">
            <h1 className="text-2xl font-extrabold text-black tracking-tight" data-id="element-1083">
              Hi {user.name.split(' ')[0]},
            </h1>
            <button className="flex items-center gap-1 text-black/80 mt-0.5 active:opacity-70 transition-opacity" data-id="element-1084">
              <MapPin size={14} className="text-black" data-id="element-1085" />
              <span className="text-sm font-medium" data-id="element-1086">Madhapur, Hyderabad</span>
              <ChevronDown size={14} data-id="element-1087" />
            </button>
          </div>
          <img src={user.avatar} alt="Profile" onClick={() => onNavigate('profile')} className="w-11 h-11 rounded-full border-2 border-black object-cover shadow-sm active:scale-95 transition-transform cursor-pointer" data-id="element-1088" />
        </div>

        {/* Search & Filter */}
        <div className="flex gap-2 mb-4" data-id="element-1089">
          <div onClick={() => onNavigate('explore')} className="flex-1 bg-white border-2 border-black rounded-full px-4 py-2.5 flex items-center gap-2 shadow-[0_2px_0_0_rgba(0,0,0,1)] active:translate-y-[2px] active:shadow-none transition-all cursor-pointer" data-id="element-1090">
            <Search size={18} className="text-black/60" data-id="element-1091" />
            <span className="text-black/60 text-sm font-medium" data-id="element-1092">
              Discover cafes near you...
            </span>
          </div>
          <button onClick={() => onNavigate('explore')} className="w-11 h-11 bg-white border-2 border-black rounded-xl flex items-center justify-center shadow-[0_2px_0_0_rgba(0,0,0,1)] active:translate-y-[2px] active:shadow-none transition-all" data-id="element-1093">
            <SlidersHorizontal size={18} className="text-black" data-id="element-1094" />
          </button>
        </div>

        {/* Category Chips */}
        <div className="flex gap-2 overflow-x-auto scrollbar-hide -mx-4 px-4 pb-1" data-id="element-1095">
          {categories.map((cat, i) => <button key={i} onClick={() => onNavigate('explore')} className="flex-shrink-0 bg-white border border-black rounded-full px-4 py-1.5 text-sm font-bold text-black shadow-[0_2px_0_0_rgba(0,0,0,1)] active:translate-y-[2px] active:shadow-none transition-all" data-id="element-1096">
              {cat}
            </button>)}
        </div>
      </div>

      {/* Content Layer */}
      <div className="relative z-10 pt-4" data-id="element-1097">
        {/* Rotating Hero Banner */}
        <div className="px-4 mb-8" data-id="element-1098">
          <div className="relative h-32 rounded-3xl overflow-hidden shadow-lg border-2 border-black" data-id="element-1099">
            {/* Slide 1: Gifting */}
            <div className={`absolute inset-0 transition-opacity duration-500 cursor-pointer ${activeHeroIndex === 0 ? 'opacity-100 z-10' : 'opacity-0 z-0 pointer-events-none'}`} style={{
            background: '#FD3DB5'
          }} onClick={() => onNavigate('gifting')} data-id="element-1100">
              <div className="absolute inset-0 flex items-center p-5" data-id="element-1101">
                <div className="flex-1" data-id="element-1102">
                  <h3 className="text-2xl font-black text-white leading-tight mb-1" data-id="element-1103">
                    Send a Gift
                  </h3>
                  <p className="text-white/90 font-medium text-sm" data-id="element-1104">
                    Treat friends to local spots
                  </p>
                </div>
                <div className="w-14 h-14 bg-black rounded-2xl flex items-center justify-center transform rotate-12 shadow-xl" data-id="element-1105">
                  <Gift size={28} className="text-[#FFFF66]" data-id="element-1106" />
                </div>
              </div>
            </div>

            {/* Slide 2: Discovery */}
            <div className={`absolute inset-0 transition-opacity duration-500 cursor-pointer ${activeHeroIndex === 1 ? 'opacity-100 z-10' : 'opacity-0 z-0 pointer-events-none'}`} style={{
            background: '#CCFF00'
          }} onClick={() => onNavigate('explore')} data-id="element-1107">
              <div className="absolute inset-0 flex items-center p-5" data-id="element-1108">
                <div className="flex-1" data-id="element-1109">
                  <h3 className="text-2xl font-black text-black leading-tight mb-1" data-id="element-1110">
                    Find Nearby
                  </h3>
                  <p className="text-black/80 font-medium text-sm" data-id="element-1111">
                    Discover hidden gems
                  </p>
                </div>
                <div className="w-14 h-14 bg-black rounded-2xl flex items-center justify-center transform -rotate-6 shadow-xl" data-id="element-1112">
                  <MapPin size={28} className="text-[#CCFF00]" data-id="element-1113" />
                </div>
              </div>
            </div>

            {/* Slide 3: Drops */}
            <div className={`absolute inset-0 transition-opacity duration-500 cursor-pointer ${activeHeroIndex === 2 ? 'opacity-100 z-10' : 'opacity-0 z-0 pointer-events-none'}`} style={{
            background: '#000000'
          }} onClick={() => onNavigate('drops')} data-id="element-1114">
              <div className="absolute inset-0 flex items-center p-5" data-id="element-1115">
                <div className="flex-1" data-id="element-1116">
                  <h3 className="text-2xl font-black text-[#FFFC00] leading-tight mb-1" data-id="element-1117">
                    New Drops
                  </h3>
                  <p className="text-white/80 font-medium text-sm" data-id="element-1118">
                    Limited time offers
                  </p>
                </div>
                <div className="w-14 h-14 bg-[#FFFC00] rounded-2xl flex items-center justify-center transform rotate-6 shadow-xl" data-id="element-1119">
                  <Sparkles size={28} className="text-black" data-id="element-1120" />
                </div>
              </div>
            </div>
          </div>

          {/* Pager Dots */}
          <div className="flex justify-center gap-1.5 mt-3" data-id="element-1121">
            {[0, 1, 2].map(i => <div key={i} className={`h-1.5 rounded-full transition-all duration-300 ${activeHeroIndex === i ? 'w-4 bg-black' : 'w-1.5 bg-black/20'}`} data-id="element-1122" />)}
          </div>
        </div>

        {/* Nearby Favorites (Promoted Up) */}
        <div className="px-4 mb-8" data-id="element-1123">
          <div className="flex justify-between items-end mb-3" data-id="element-1124">
            <div data-id="element-1125">
              <h2 className="text-xl font-black text-black tracking-tight" data-id="element-1126">
                Nearby Favorites
              </h2>
              <p className="text-sm text-black/60 font-medium" data-id="element-1127">
                Closest spots to you
              </p>
            </div>
            <button onClick={() => onNavigate('explore')} className="text-sm text-black font-bold flex items-center bg-white px-3 py-1 rounded-full border border-black shadow-[0_2px_0_0_rgba(0,0,0,1)] active:translate-y-[2px] active:shadow-none" data-id="element-1128">
              See All <ChevronRight size={14} className="ml-0.5" data-id="element-1129" />
            </button>
          </div>
          <div className="flex gap-4 overflow-x-auto pb-4 -mx-4 px-4 scrollbar-hide" data-id="element-1130">
            {nearbyStores.map(store => <StoreCard key={store.id} store={store} variant="compact" onClick={() => onStoreClick(store)} data-id="element-1131" />)}
          </div>
        </div>

        {/* Your Rewards (Cold-Start Aware) */}
        <div className="px-4 mb-8" data-id="element-1132">
          <div className="flex items-center justify-between mb-3" data-id="element-1133">
            <h2 className="text-xl font-black text-black tracking-tight" data-id="element-1134">
              Your Rewards
            </h2>
            {hasHistory && <div className="flex items-center text-black/60 text-xs font-bold uppercase tracking-wider gap-1" data-id="element-1135">
                <span data-id="element-1136">Swipe</span>
                <ChevronRight size={14} data-id="element-1137" />
              </div>}
          </div>

          {hasHistory ? <div className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide snap-x snap-mandatory -mx-4 px-4" data-id="element-1138">
              {rewardStores.map(rewardData => <div key={rewardData.store.id} className="flex-shrink-0 w-[85%] snap-start" data-id="element-1139">
                  <div className="bg-white rounded-3xl p-5 shadow-lg border-2 border-black" data-id="element-1140">
                    <div className="flex items-start justify-between mb-3" data-id="element-1141">
                      <h3 className="text-xl font-bold text-black" data-id="element-1142">
                        {rewardData.store.name}
                      </h3>
                      <span className="bg-[#FFFC00] text-black text-xs font-bold px-3 py-1 rounded-full border border-black" data-id="element-1143">
                        {rewardData.discount}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-black/60 text-sm mb-4 font-medium" data-id="element-1144">
                      <Calendar size={14} data-id="element-1145" />
                      <span data-id="element-1146">Last visit: {rewardData.lastVisit}</span>
                    </div>
                    <div className="bg-[#FFFF66]/30 rounded-2xl p-4 mb-4 border border-black/10" data-id="element-1147">
                      <p className="text-black font-bold text-sm" data-id="element-1148">
                        {rewardData.reward}
                      </p>
                    </div>
                    <button onClick={() => onStoreClick(rewardData.store)} className="w-full bg-black text-[#FFFC00] font-bold py-3 rounded-2xl transition-transform active:scale-95" data-id="element-1149">
                      Redeem on Next Visit
                    </button>
                  </div>
                </div>)}
            </div> : <div className="bg-white rounded-3xl p-6 shadow-lg border-2 border-black text-center" data-id="element-1150">
              <div className="w-16 h-16 bg-[#FFFF66] rounded-full flex items-center justify-center mx-auto mb-4 border-2 border-black shadow-[0_4px_0_0_rgba(0,0,0,1)]" data-id="element-1151">
                <Coffee size={28} className="text-black" data-id="element-1152" />
              </div>
              <h3 className="text-lg font-bold text-black mb-2" data-id="element-1153">
                Start earning rewards
              </h3>
              <p className="text-sm text-black/60 font-medium mb-5 px-4" data-id="element-1154">
                Discover local cafes near you and unlock exclusive rewards as
                you explore.
              </p>
              <button onClick={() => onNavigate('explore')} className="w-full bg-black text-[#FFFC00] font-bold py-3.5 rounded-xl active:scale-95 transition-transform" data-id="element-1155">
                Explore Places Near You
              </button>
            </div>}
        </div>

        {/* Curated Rail: Worth the Trip */}
        <div className="px-4 mb-8" data-id="element-1156">
          <div className="flex justify-between items-end mb-3" data-id="element-1157">
            <div className="flex items-center gap-2" data-id="element-1158">
              <div className="w-8 h-8 rounded-full bg-[#FD3DB5] flex items-center justify-center border border-black shadow-[0_2px_0_0_rgba(0,0,0,1)]" data-id="element-1159">
                <TrendingUp size={16} className="text-white" data-id="element-1160" />
              </div>
              <div data-id="element-1161">
                <h2 className="text-xl font-black text-black tracking-tight" data-id="element-1162">
                  Worth the Trip
                </h2>
                <p className="text-sm text-black/60 font-medium" data-id="element-1163">
                  Highly rated destinations
                </p>
              </div>
            </div>
          </div>
          <div className="flex gap-4 overflow-x-auto pb-4 -mx-4 px-4 scrollbar-hide" data-id="element-1164">
            {worthTheTrip.map(store => <StoreCard key={store.id} store={store} variant="compact" onClick={() => onStoreClick(store)} data-id="element-1165" />)}
          </div>
        </div>

        {/* Curated Rail: Friend Lists */}
        {friendLists.length > 0 && <div className="px-4 mb-8" data-id="element-1166">
            <div className="flex justify-between items-end mb-3" data-id="element-1167">
              <div className="flex items-center gap-2" data-id="element-1168">
                <div className="w-8 h-8 rounded-full bg-[#CCFF00] flex items-center justify-center border border-black shadow-[0_2px_0_0_rgba(0,0,0,1)]" data-id="element-1169">
                  <Heart size={16} className="text-black" data-id="element-1170" />
                </div>
                <div data-id="element-1171">
                  <h2 className="text-xl font-black text-black tracking-tight" data-id="element-1172">
                    Friend Curated
                  </h2>
                  <p className="text-sm text-black/60 font-medium" data-id="element-1173">
                    Lists shared with you
                  </p>
                </div>
              </div>
              <button onClick={() => onNavigate('favoritePlaces')} className="text-sm text-black font-bold flex items-center bg-white px-3 py-1 rounded-full border border-black shadow-[0_2px_0_0_rgba(0,0,0,1)] active:translate-y-[2px] active:shadow-none" data-id="element-1174">
                All Lists <ChevronRight size={14} className="ml-0.5" data-id="element-1175" />
              </button>
            </div>
            <div className="flex gap-4 overflow-x-auto pb-4 -mx-4 px-4 scrollbar-hide" data-id="element-1176">
              {friendLists.map(list => <div key={list.id} onClick={() => onNavigate('favoritePlaces')} className="flex-shrink-0 w-48 bg-white rounded-2xl border-2 border-black p-3 shadow-[0_4px_0_0_rgba(0,0,0,1)] active:translate-y-[4px] active:shadow-none transition-all cursor-pointer" data-id="element-1177">
                  <div className="flex gap-1 mb-3" data-id="element-1178">
                    {list.places.slice(0, 3).map((place, i) => <div key={i} className="h-16 flex-1 bg-gray-200 rounded-lg overflow-hidden border border-black/10" data-id="element-1179">
                        <img src={place.image} alt="" className="w-full h-full object-cover" data-id="element-1180" />
                      </div>)}
                  </div>
                  <h3 className="font-bold text-black text-sm truncate" data-id="element-1181">
                    {list.name}
                  </h3>
                  <p className="text-xs text-black/60 font-medium mt-0.5" data-id="element-1182">
                    By {list.authorName} • {list.places.length} places
                  </p>
                </div>)}
            </div>
          </div>}

        {/* Curated Rail: New on Kraya */}
        <div className="px-4 mb-8" data-id="element-1183">
          <div className="flex justify-between items-end mb-3" data-id="element-1184">
            <div className="flex items-center gap-2" data-id="element-1185">
              <div className="w-8 h-8 rounded-full bg-black flex items-center justify-center shadow-[0_2px_0_0_rgba(0,0,0,0.2)]" data-id="element-1186">
                <Sparkles size={16} className="text-[#FFFC00]" data-id="element-1187" />
              </div>
              <div data-id="element-1188">
                <h2 className="text-xl font-black text-black tracking-tight" data-id="element-1189">
                  New on Kraya
                </h2>
                <p className="text-sm text-black/60 font-medium" data-id="element-1190">
                  Fresh spots to explore
                </p>
              </div>
            </div>
          </div>
          <div className="flex gap-4 overflow-x-auto pb-4 -mx-4 px-4 scrollbar-hide" data-id="element-1191">
            {newOnKraya.map(store => <StoreCard key={store.id} store={store} variant="compact" onClick={() => onStoreClick(store)} data-id="element-1192" />)}
          </div>
        </div>
      </div>
    </div>;
}